import React from 'react';
import { useDispatch } from 'react-redux';
import { Body7, Container, Select, TextField } from '@ui';
import { FlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { showSuccessBanner } from '@utils/application';
import { correctExtractionRow } from '@services/fmdc';
import { useVocabularies, vocabularyOptions } from './FmdcTenantContext';
import { CatalogItemPicker } from './CatalogItemPicker';
import { NoticeText, NOTICE_TONES } from './NoticeText';
import { Card, SectionHead } from './styles';

// Correct one extracted value's classification: its item, split, metric component, scenario.
// Only the fields that differ from the row are sent — the API answers 422 for "nothing changed",
// so the button stays inert until something does. Scenario applies to this one cell; the other
// fields carry over to the issuer's next upload, and the hint says so.

const FIELD_KEYS = ['canonical_item_code', 'split_type', 'split_label', 'metric_component', 'scenario'];

// The row's current classification, read defensively: the payload shape nests split in an object,
// the extraction_rows shape carries the template's flat columns.
export const currentFields = (row) => ({
  canonical_item_code: row?.canonical_item_code ?? row?.item ?? '',
  split_type: row?.split?.type ?? row?.split_type ?? '',
  split_label: row?.split?.label ?? row?.split_label ?? '',
  metric_component: row?.metric_component ?? '',
  scenario: row?.scenario ?? '',
});

const normalize = (value) => (value === undefined || value === null ? '' : String(value));

// Only the keys whose value differs from the row.
export const changedFields = (original, draft) =>
  FIELD_KEYS.reduce((patch, key) => {
    if (normalize(original[key]) !== normalize(draft[key])) patch[key] = draft[key] === '' ? null : draft[key];
    return patch;
  }, {});

// A value the row holds that the vocabulary does not list is still the value on the row: offer it
// so the select shows the truth rather than a blank (the ModelSelect rule).
const withCurrent = (options, value) => {
  if (!value || options.some((o) => o.value === value)) return options;
  return [{ value, label: `${value} (not in vocabulary)` }, ...options];
};

export const CorrectForm = ({ row, catalogItem, onCorrected }) => {
  const dispatch = useDispatch();
  const { vocabularies, error: vocabulariesError, isLoading: vocabulariesLoading } = useVocabularies();

  const original = React.useMemo(() => currentFields(row), [row]);
  const [draft, setDraft] = React.useState(original);
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [notices, setNotices] = React.useState([]);

  // A corrected row arrives as a new `row`; the draft follows it so the form never shows a stale
  // value as "changed". Keyed on the field VALUES: a refetch after a decide hands down a new row
  // object with the same classification, and a reset on identity threw away a correction
  // mid-composition.
  const originalKey = JSON.stringify(original);
  React.useEffect(() => {
    setDraft(original);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalKey]);

  const patch = changedFields(original, draft);
  const hasChanges = Object.keys(patch).length > 0;
  const setField = (key) => (value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const options = (listName, current) => withCurrent(vocabularyOptions(vocabularies, listName), current);

  const save = async () => {
    if (busy || !hasChanges) return;
    setBusy(true);
    setError(null);
    setNotices([]);
    const body = { ...patch };
    if (reason.trim()) body.reason = reason.trim();
    const result = await correctExtractionRow(row.id, body);
    setBusy(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    const next = [];
    if (result.result?.natural_key_clash) {
      next.push({
        key: 'clash',
        tone: NOTICE_TONES.WARNING,
        text: 'This now duplicates another row’s key; it will go to review as a duplicate.',
      });
    }
    if (result.result?.mapping_saved === false) {
      next.push({
        key: 'mapping',
        tone: NOTICE_TONES.WARNING,
        text: `Corrected, but not saved for the issuer${
          result.result.mapping_note ? `: ${result.result.mapping_note}` : '.'
        }`,
      });
    } else if (result.result?.mapping_saved === true) {
      next.push({
        key: 'mapping',
        tone: NOTICE_TONES.POSITIVE,
        text: 'Saved for the issuer: the next upload applies it.',
      });
    }
    setNotices(next);
    setReason('');
    showSuccessBanner('Correction saved', null, dispatch);
    if (onCorrected) onCorrected(result.result);
  };

  return (
    <Card>
      <SectionHead
        heading="Correct"
        subtext="Change what this value is. The item, split and metric carry over to the issuer's next upload."
      />

      <Container top={16}>
        <CatalogItemPicker
          label="Item"
          value={draft.canonical_item_code}
          selectedItem={
            catalogItem && catalogItem.canonical_item_code === draft.canonical_item_code ? catalogItem : null
          }
          onChange={(code) => setField('canonical_item_code')(code)}
        />
      </Container>

      {vocabulariesLoading && (
        <Container top={12}>
          <Body7 isLowContrast role="status">
            Loading vocabularies…
          </Body7>
        </Container>
      )}
      {vocabulariesError && (
        <Container top={12}>
          <ErrorText>{vocabulariesError}</ErrorText>
        </Container>
      )}

      <Container top={16}>
        <Select
          label="Split type"
          value={draft.split_type}
          onChange={setField('split_type')}
          options={options('split_type', draft.split_type)}
          placeholder="None"
        />
      </Container>
      <Container top={16}>
        <TextField label="Split label" value={draft.split_label} onChange={setField('split_label')} />
      </Container>
      <Container top={16}>
        <Select
          label="Metric component"
          value={draft.metric_component}
          onChange={setField('metric_component')}
          options={options('metric_component', draft.metric_component)}
          placeholder="None"
        />
      </Container>
      <Container top={16}>
        <Select
          label="Scenario"
          value={draft.scenario}
          onChange={setField('scenario')}
          options={options('scenario', draft.scenario)}
          placeholder="None"
          hint="Applies to this cell only; the other fields carry over to the issuer's next upload."
        />
      </Container>
      <Container top={16}>
        <TextField label="Reason" value={reason} onChange={setReason} multiline rows={2} placeholder="Optional" />
      </Container>

      {error && (
        <Container top={12}>
          <ErrorText>{error}</ErrorText>
        </Container>
      )}
      {notices.map((notice) => (
        <Container top={12} key={notice.key}>
          <NoticeText tone={notice.tone}>{notice.text}</NoticeText>
        </Container>
      ))}

      <Container top={24} row verticallyCenter gap={12}>
        <FlatButton
          onClick={save}
          disabled={busy || !hasChanges}
          buttonProps={{ 'aria-disabled': busy || !hasChanges }}
        >
          {busy ? 'Saving…' : 'Save correction'}
        </FlatButton>
        {!hasChanges && !busy && <Body7 isLowContrast>Change a field to save.</Body7>}
      </Container>
    </Card>
  );
};
